import { useState } from "react";
import { jod } from "@astroplanet/shared";
import type { PaymentMethod } from "../../lib/types";
import { CashTender, PaymentPicker } from "./PaymentPicker";

export interface PaymentForm {
  method: PaymentMethod;
  setMethod: (m: PaymentMethod) => void;
  tendered: string;
  setTendered: (v: string) => void;
  /** Payments covering `total` exactly (empty when nothing is due). */
  payments: (total: number) => Array<{ method: PaymentMethod; amount: number }>;
  /** False while a cash amount smaller than the total has been typed. */
  isValid: (total: number) => boolean;
  reset: () => void;
}

export function usePaymentForm(initial: PaymentMethod = "cash"): PaymentForm {
  const [method, setMethod] = useState<PaymentMethod>(initial);
  const [tendered, setTendered] = useState("");
  return {
    method,
    setMethod: (m) => {
      setMethod(m);
      setTendered("");
    },
    tendered,
    setTendered,
    payments: (total) => (total > 0 ? [{ method, amount: total }] : []),
    isValid: (total) => {
      if (total <= 0 || method !== "cash" || tendered.trim() === "") return true;
      const given = jod(Number(tendered));
      return !Number.isNaN(given) && given >= total;
    },
    reset: () => setTendered(""),
  };
}

interface Props {
  form: PaymentForm;
  total: number;
  methods?: PaymentMethod[];
}

/** Method buttons + (for cash) a received-amount box that shows the change due. */
export function PaymentSection({ form, total, methods }: Props) {
  if (total <= 0) return null;
  return (
    <div className="space-y-2">
      <PaymentPicker value={form.method} onChange={form.setMethod} methods={methods} />
      {form.method === "cash" && <CashTender total={total} tendered={form.tendered} onTendered={form.setTendered} />}
    </div>
  );
}
